import React from "react";
import { CommonFeaturesProviderProps, CommonFeaturesType } from "./type";
import useAuthenticationAlert from "shared/hooks/useAuthenticationAlert";
import useConfirmationAlert from "../../shared/hooks/useConfirmationAlert";
import Loader from "../../shared/components/loader/loader";
import DisplayFullPageError from "shared/components/fullPageError";
import { DisplayFullPageErrorArgs } from "shared/components/fullPageError/types";

const Context = React.createContext<CommonFeaturesType>({} as CommonFeaturesType);

function CommonFeaturesProvider(props: CommonFeaturesProviderProps) {
  const { confirmationAlertProps, initConfirmationAlert, alertComponent } = useConfirmationAlert();
  const { authenticationAlertProps, initAuthenticationAlert, authenticationAlertComponent } = useAuthenticationAlert();
  const [showLoader, setShowLoader] = React.useState<boolean>(false);
  const [fullPageErrorConfig, setFullPageErrorConfig] = React.useState<DisplayFullPageErrorArgs>();

  const showLoadingIndicator = React.useCallback((value: boolean) => {
    setShowLoader(value);
  }, []);

  const showFullPageError = React.useCallback((args: DisplayFullPageErrorArgs) => {
    setFullPageErrorConfig({
      ...args,
      closeHandler: () => {
        args.closeHandler();
        setFullPageErrorConfig(undefined);
      }
    });
  }, []);

  const closeFullPageError = React.useCallback(() => {
    setFullPageErrorConfig(undefined);
  }, []);

  return (
    <Context.Provider
      value={{
        confirmationAlertProps,
        initConfirmationAlert,
        authenticationAlertProps,
        initAuthenticationAlert,
        showLoadingIndicator,
        showFullPageError,
        closeFullPageError
      }}>
      {alertComponent}
      {authenticationAlertComponent}
      {showLoader && <Loader />}
      {fullPageErrorConfig && <DisplayFullPageError {...fullPageErrorConfig} />}
      {props.children}
    </Context.Provider>
  );
}

function useCommonFeatures() {
  const context = React.useContext(Context);
  if (context === undefined) {
    throw new Error(`useCommonFeatures must be used within a CommonFeaturesProvider`);
  }
  return context;
}

export { CommonFeaturesProvider, useCommonFeatures };
